import CT from '@marketplace-ai/tags';
import { useWatch } from 'react-hook-form';
import { formatCalculatedMoney } from '~/features/market/listings/components/costCalculatorMoney';
import { calculateProposedContractValue, getContractValueDifference } from '../../contractValue';
import { useNegotiationBaseline, useNegotiationResponseType } from '../../provider';
import { BaseCompensationSection } from './BaseCompensationSection';
import { ShiftDifferentialsSection } from './ShiftDifferentialsSection';

const TAGS = CT.employer.negotiation.compensationForm;

const formatMoneyOrDash = (value: number | string | null | undefined) =>
  value == null || value === '' ? '---' : formatCalculatedMoney(value);

const formatDifference = (difference: string) => {
  const sign = Number(difference) > 0 ? '+ ' : Number(difference) < 0 ? '- ' : '';
  return `${sign}${formatCalculatedMoney(difference.replace('-', ''))} difference`;
};

export const Compensation = () => {
  const negotiation = useNegotiationBaseline();
  const responseType = useNegotiationResponseType();
  const formValues = useWatch();
  // [MCV-1770] Priced from the same merge the Submit Offer / Counter handlers
  // send (NegotiationDetails handleSubmitOffer / handleCounterClick).
  const proposed = calculateProposedContractValue({ ...negotiation, ...formValues }, responseType);
  const difference = getContractValueDifference(
    proposed?.totalContractValue,
    negotiation?.totalContractValue
  );

  return (
    <div className="mt-4">
      <BaseCompensationSection />
      <ShiftDifferentialsSection />
      <div className="mt-6 p-4 rounded-lg bg-primary-t-900">
        <h3 className="sub-heading-sb text-center mb-5">Calculated Contract Value</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="justify-start text-left">
            <p className="text-sm text-grey-400">Previous Total value</p>
            <p className="text-lg font-bold text-grey-400" data-cy={TAGS.previousTotalValue}>
              {formatMoneyOrDash(negotiation?.totalContractValue)}
            </p>
            <p className="text-sm text-grey-400" data-cy={TAGS.previousAvgHourlyRate}>
              Previous Average Hourly Rate: {formatMoneyOrDash(negotiation?.avgHourlyRate)}
            </p>
          </div>
          <div className="justify-end text-left mr-8 border border-warning-500 bg-warning-50 pl-8 p-4 rounded-lg">
            <p className="text-sm text-grey-400">Your Proposed Total value</p>
            <p className="text-lg font-bold" data-cy={TAGS.proposedTotalValue}>
              {formatMoneyOrDash(proposed?.totalContractValue)}
            </p>
            <p className="text-sm text-grey-400" data-cy={TAGS.proposedAvgHourlyRate}>
              Your Proposed Average Hourly Rate: {formatMoneyOrDash(proposed?.avgHourlyRate)}
            </p>
            {difference !== null && (
              <p className="text-sm text-grey-400" data-cy={TAGS.proposedValueDifference}>
                {formatDifference(difference)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
